import { useEffect, useState } from 'react';
import { Time } from './types';
import { FULL_DASH_ARRAY, calculateProgress, getMilestone, Milestone, formatCountdown } from '../../utils/clockUtils';

interface ClockProps {
  time: Time;
}

const Clock = ({ time }: ClockProps) => {
  const [showMilestone, setShowMilestone] = useState(false);
  const [lastMilestone, setLastMilestone] = useState(0);

  const minutes = time.totalSessionTime;
  const elapsedSeconds = time.initialMinutes * 60 - time.secondsRemaining;
  const seconds = Math.floor(elapsedSeconds % 60);
  const progress = calculateProgress(seconds);
  const milestone: Milestone | null = getMilestone(minutes);

  useEffect(() => {
    if (milestone && minutes > lastMilestone && [5, 15, 25].includes(minutes)) {
      setShowMilestone(true);
      setLastMilestone(minutes);
      const timer = setTimeout(() => setShowMilestone(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [minutes, milestone, lastMilestone]);

  const strokeColor = milestone ? milestone.color : '#3b2e1e'; // leather

  return (
    <div className="text-center">
      <div className="relative inline-block w-[200px] h-[200px]">
        <svg
          className="[transform:scaleX(-1)]"
          viewBox="0 0 180 180"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g style={{ fill: 'none', stroke: 'none' }}>
            <circle style={{ strokeWidth: '10', stroke: '#eeeeee' }} cx="90" cy="90" r="85" />
            <path
              className="[transform:rotate(90deg)] [transform-origin:center]"
              strokeDasharray={`${(progress * FULL_DASH_ARRAY).toFixed(0)} 534`}
              style={{
                strokeWidth: '10',
                strokeLinecap: 'round',
                transition: '1s linear all',
                stroke: strokeColor,
                fill: 'none',
              }}
              d="M 90, 90 m -85, 0 a 85,85 0 1,0 170,0 a 85,85 0 1,0 -170,0"
            />
          </g>
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
          <div
            className={`text-5xl font-bold text-leather ${showMilestone ? 'animate-[pulse_0.5s_ease-in-out]' : ''}`}
          >
            {minutes}
          </div>
          <div className="text-xs text-ink-muted mt-1">min read</div>
          {milestone && (
            <span
              className={`mt-1 text-xs font-semibold px-2 py-0.5 rounded-full text-white ${showMilestone ? 'animate-[pop_0.6s_cubic-bezier(0.68,-0.55,0.265,1.55)]' : ''}`}
              style={{ backgroundColor: milestone.color }}
            >
              {milestone.emoji} {milestone.text}
            </span>
          )}
        </div>
      </div>

      {typeof time.secondsRemaining === 'number' && !isNaN(time.secondsRemaining) && (
        <div className="mt-2 text-sm text-ink-muted">
          Check-in in {formatCountdown(time.secondsRemaining)}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        @keyframes pop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Clock;
